import { useState, useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Button,
  Col,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Skeleton,
  Tag,
  Typography,
} from "antd";
import { EditOutlined, KeyOutlined } from "@ant-design/icons";
import { actions as routerActions } from "redux-router5";
import type { Dispatch } from "@reduxjs/toolkit";
import type { State as RouterState } from "router5";
import type { IntlShape } from "@formatjs/intl";
import ConnectedAppLayout from "../AppLayout";
import { PageTitleContainer } from "../../common/components/PageTitle";
import { Card } from "../../common/components/Card";
import { routeNames } from "../common/routes";
import type { RootState } from "../store";
import type { CurrentUser, UserGroup, UserProfile } from "../generated-axios-client";
import { palette } from "../theme";
import { userActions } from "../../features/user/userSlice";
import { isAdministrator } from "../../common/utils/isAdministrator";

const AVATAR_PALETTE = [
  palette.dashBlue,
  palette.dashGreen,
  palette.dashOrange,
  palette.dashPurple,
  palette.dashCyan,
  palette.dashRed,
];

function getInitials(profile: UserProfile): string {
  const first = profile.first_name?.trim();
  const last = profile.last_name?.trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  return profile.username.slice(0, 2).toUpperCase();
}

function getDisplayName(profile: UserProfile): string {
  const parts = [profile.first_name?.trim(), profile.last_name?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : profile.username;
}

type Props = {
  dispatch: Dispatch;
  route: RouterState | null;
  currentUser: CurrentUser | undefined;
  intl: IntlShape;
};

export function UserProfilePage({ dispatch, route, currentUser, intl }: Props) {
  const reduxDispatch = useDispatch();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm] = Form.useForm();

  const profileId = route?.params?.id as string | undefined;
  const profile = useSelector((state: RootState) => state.userstate.viewedProfile) as UserProfile | null;
  const groups = useSelector((state: RootState) => state.userstate.groups) as UserGroup[];
  const changePasswordStatus = useSelector((state: RootState) => state.userstate.changePasswordStatus);
  const loading = profile === null;

  useEffect(() => {
    if (changePasswordStatus === "succeeded") {
      setShowPasswordModal(false);
      passwordForm.resetFields();
      reduxDispatch(userActions.setChangePasswordStatus("idle"));
    }
  }, [changePasswordStatus, passwordForm, reduxDispatch]);

  if (!route) return null;

  const isOwnProfile = currentUser?.id === profile?.id;
  const isAdmin = isAdministrator(currentUser?.groups);
  const canEdit = isOwnProfile || isAdmin;
  const canChangePassword = isOwnProfile;

  const groupName =
    profile?.groups && profile.groups.length > 0
      ? groups.find((g) => g.id === profile.groups![0])?.name ?? null
      : null;

  const avatarColor =
    profile ? AVATAR_PALETTE[profile.id % AVATAR_PALETTE.length] : AVATAR_PALETTE[0];

  const submitPasswordChange = () => {
    passwordForm
      .validateFields()
      .then((values) => {
        if (values.newPassword !== values.confirmPassword) {
          passwordForm.setFields([
            {
              name: "confirmPassword",
              errors: [intl.formatMessage({ id: "profile_change_password_mismatch" })],
            },
          ]);
          return;
        }
        reduxDispatch(userActions.changePassword({
          profileId: profileId!,
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        }));
      })
      .catch(() => {});
  };

  const actionButtons = (
    <Flex gap={8}>
      {canEdit && (
        <Button
          icon={<EditOutlined />}
          onClick={() =>
            dispatch(
              routerActions.navigateTo(routeNames.USER_PROFILE_EDIT, { id: profileId })
            )
          }
        >
          {intl.formatMessage({ id: "profile_edit_button" })}
        </Button>
      )}
      {canChangePassword && (
        <Button icon={<KeyOutlined />} onClick={() => setShowPasswordModal(true)}>
          {intl.formatMessage({ id: "profile_change_password" })}
        </Button>
      )}
    </Flex>
  );

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={
          loading
            ? intl.formatMessage({ id: "breadcrumb_users_view" })
            : getDisplayName(profile!)
        }
        extra={!loading && profile ? actionButtons : undefined}
      >
        {loading ? (
          <Skeleton active />
        ) : profile ? (
          <>
            <Card>
              <Flex vertical align="center" gap={12} style={{ paddingBottom: 24 }}>
                <Avatar
                  size={80}
                  style={{ backgroundColor: avatarColor, fontSize: 28, fontWeight: 700 }}
                >
                  {getInitials(profile)}
                </Avatar>
                <Flex vertical align="center" gap={4}>
                  <Typography.Title level={3} style={{ margin: 0 }}>
                    {getDisplayName(profile)}
                  </Typography.Title>
                  <Typography.Text type="secondary">@{profile.username}</Typography.Text>
                  {currentUser?.is_staff && profile.id === currentUser.id && (
                    <Tag color="blue">
                      {intl.formatMessage({ id: "profile_staff_badge" })}
                    </Tag>
                  )}
                </Flex>
              </Flex>

              <Row gutter={[0, 0]}>
                <Col xs={24}>
                  <Descriptions
                    bordered
                    column={{ xs: 1, sm: 2 }}
                    title={intl.formatMessage({ id: "profile_section_info" })}
                  >
                    <Descriptions.Item
                      label={intl.formatMessage({ id: "profile_first_name" })}
                    >
                      {profile.first_name || intl.formatMessage({ id: "profile_no_name" })}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={intl.formatMessage({ id: "profile_last_name" })}
                    >
                      {profile.last_name || intl.formatMessage({ id: "profile_no_name" })}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={intl.formatMessage({ id: "profile_username" })}
                    >
                      {profile.username}
                    </Descriptions.Item>
                    <Descriptions.Item label={intl.formatMessage({ id: "profile_email" })}>
                      <Flex gap={8} align="center">
                        {profile.email || intl.formatMessage({ id: "profile_no_name" })}
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          ({intl.formatMessage({ id: "profile_email_readonly_hint" })})
                        </Typography.Text>
                      </Flex>
                    </Descriptions.Item>
                    <Descriptions.Item label={intl.formatMessage({ id: "profile_phone" })}>
                      {profile.phone_number || intl.formatMessage({ id: "profile_no_name" })}
                    </Descriptions.Item>
                    <Descriptions.Item label={intl.formatMessage({ id: "profile_role" })}>
                      {groupName ? (
                        <Tag>{groupName}</Tag>
                      ) : (
                        intl.formatMessage({ id: "profile_no_name" })
                      )}
                    </Descriptions.Item>
                    {profile.technician_level && (
                      <Descriptions.Item
                        label={intl.formatMessage({ id: "profile_technician_level" })}
                      >
                        <Tag color="blue">
                          {intl.formatMessage({
                            id: `user_technician_level_${profile.technician_level.toLowerCase()}`,
                          })}
                        </Tag>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            <Modal
              open={showPasswordModal}
              title={intl.formatMessage({ id: "profile_change_password_title" })}
              onCancel={() => {
                setShowPasswordModal(false);
                passwordForm.resetFields();
              }}
              onOk={submitPasswordChange}
              okText={intl.formatMessage({ id: "ticket_button_title_create" })}
              cancelText={intl.formatMessage({ id: "ticket_button_title_cancel" })}
              confirmLoading={changePasswordStatus === "loading"}
              destroyOnHidden
            >
              <Form form={passwordForm} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item
                  name="oldPassword"
                  label={intl.formatMessage({ id: "profile_change_password_old" })}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "profile_change_password_old_required",
                      }),
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Form.Item
                  name="newPassword"
                  label={intl.formatMessage({ id: "profile_change_password_new" })}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "profile_change_password_new_required",
                      }),
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Form.Item
                  name="confirmPassword"
                  label={intl.formatMessage({ id: "profile_change_password_confirm" })}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "profile_change_password_confirm_required",
                      }),
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </Form>
            </Modal>
          </>
        ) : (
          <Typography.Text type="secondary">User not found.</Typography.Text>
        )}
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
}

export const ConnectedUserProfilePage = connect((state: RootState) => ({
  route: state.router.route,
  currentUser: state.userstate.user,
  intl: state.intl.datetimeIntl,
}))(UserProfilePage);

export default ConnectedUserProfilePage;
